import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useMarketListQuery } from "Market/Api/Query/useMarketListQuery";
import { useMarket } from "Market/Providers/CurrentMarketProvider";
import { usePartnersListTable } from "Partner/Components/Tables/Hooks/usePartnersListTable";
import { usePartnerFilters } from "Partner/Hooks/usePartnerFilters";
import { PartnerPaths } from "Partner/Routes/Routing";
import { PartnerFilterWidget } from "Partner/Widgets/PartnerFilterWidget";
import { PageContainer } from "Shared/Components/Container";
import { Table } from "Shared/Components/Table";
import { Add } from "@mui/icons-material";
import {
	Box,
	Button,
	ButtonGroup,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router";

const partnerResource = Resources.base.partner.shared;

// "0" selects All markets (no country filter)
const ALL_MARKETS = "0";

export const PartnersPage = () => {
	const { t } = useResource();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { market } = useMarket();
	const { data: markets } = useMarketListQuery();
	const filter = usePartnerFilters();
	const tableOptions = usePartnersListTable(filter);

	const selectedMarket =
		searchParams.get("marketId") ??
		(market?.id ? String(market.id) : ALL_MARKETS);

	const handleMarketChange = (value: string) => {
		const next = new URLSearchParams(searchParams);
		if (value === ALL_MARKETS) {
			next.set("marketId", ALL_MARKETS);
		} else {
			next.set("marketId", value);
		}
		setSearchParams(next);
	};

	return (
		<PageContainer
			title={t(partnerResource.heading)}
			description="List of partners"
		>
			<Stack
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<Typography variant="h2" mb={2}>
					{t(partnerResource.heading)}
				</Typography>
				<Stack flexDirection="row" alignItems="center" spacing={1}>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel id="partner-market-select-label">
							{t(partnerResource.country)}
						</InputLabel>
						<Select
							labelId="partner-market-select-label"
							label={t(partnerResource.country)}
							value={selectedMarket}
							onChange={(e) => handleMarketChange(e.target.value)}
						>
							<MenuItem value={ALL_MARKETS}>
								{t(partnerResource.allMarkets)}
							</MenuItem>
							{markets
								?.filter((m) => m.isActive)
								.map((m) => (
									<MenuItem key={m.id} value={String(m.id)}>
										{m.name}
									</MenuItem>
								))}
						</Select>
					</FormControl>
					<ButtonGroup size="small" variant="contained">
						<PartnerFilterWidget />
						<Button onClick={() => navigate(PartnerPaths.Create.route)}>
							<Add fontSize="small" />
						</Button>
					</ButtonGroup>
				</Stack>
			</Stack>

			<Box mt={2}>
				<Table tableOptions={tableOptions} />
			</Box>
		</PageContainer>
	);
};
